/**
 * Sensor data conversion utilities
 */
/**
 * Default pH calibration values (theoretical Nernst values)
 */
export const DEFAULT_PH_CALIBRATION = {
    sensitivityMvPerPh: -59.16,
    offsetMv: 1114.12
};
/**
 * Convert pH voltage (mV) to pH value using calibration
 * Formula: pH = (v - offset) / sensitivity
 *
 * @param voltageMv - Voltage reading in mV from ISFET sensor
 * @param temperatureC - Temperature in Celsius from thermistor (currently unused)
 * @param calibration - Calibration parameters (defaults to theoretical values)
 * @returns Calibrated pH value
 */
export function calculateCalibratedPh(voltageMv, temperatureC, calibration = DEFAULT_PH_CALIBRATION) {
    if (calibration.sensitivityMvPerPh === 0)
        return 0;
    const ph = (voltageMv - calibration.offsetMv) / calibration.sensitivityMvPerPh;
    return Number.isFinite(ph) ? ph : 0;
}
/**
 * Convert pH voltage (mV) to pH value
 * Uses Nernst equation: pH = 7.0 - (mV - offset) / slope
 * Slope is ~59.16 mV/pH at 25°C, offset assumes 700mV = pH 7
 * @deprecated Use calculateCalibratedPh for calibration-aware conversion
 */
export function phVoltageToValue(mv) {
    const offset = 700; // mV at pH 7
    const slope = 59.16; // mV per pH unit at 25°C
    return 7.0 - (mv - offset) / slope;
}
/**
 * Convert raw temperature ADC value to Celsius
 * Firmware uses 1/128 °C units
 */
export function tempRawToCelsius(raw) {
    const c = raw / 128;
    return Number.isFinite(c) ? c : 0;
}
/**
 * Format device timestamp (milliseconds) to Date
 */
export function formatDeviceTimestamp(timestamp) {
    return new Date(timestamp);
}
/**
 * Calculate linear regression for calibration data
 * Uses least squares method to find best fit line y = mx + b
 * where x = pH and y = voltage_mv
 *
 * @param points Array of {x: pH, y: voltage_mv} points
 * @returns LinearRegressionResult with slope (sensitivity), yIntercept (offset), and rSquared (linearity)
 */
export function calculateLinearRegression(points) {
    const n = points.length;
    if (n === 0) {
        return { slope: 0, yIntercept: 0, rSquared: 0 };
    }
    if (n === 1) {
        return { slope: 0, yIntercept: points[0].y, rSquared: 1 };
    }
    // Calculate sums for least squares
    const sumX = points.reduce((s, p) => s + p.x, 0);
    const sumY = points.reduce((s, p) => s + p.y, 0);
    const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
    const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
    const sumY2 = points.reduce((s, p) => s + p.y * p.y, 0);
    // Calculate slope (m) and y-intercept (b) using least squares formulas
    const denominator = n * sumX2 - sumX * sumX;
    const slope = denominator !== 0 ? (n * sumXY - sumX * sumY) / denominator : 0;
    const yIntercept = (sumY - slope * sumX) / n;
    // Calculate R² (coefficient of determination)
    const numerator = n * sumXY - sumX * sumY;
    const rDenominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    const r = rDenominator !== 0 ? numerator / rDenominator : 0;
    const rSquared = r * r;
    return { slope, yIntercept, rSquared };
}
